import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle, FileText, Video, MessageCircle, ExternalLink, ChevronRight } from 'lucide-react';
import api from '../../lib/api';
import { Job, Invitation } from '../../types';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import StepTabsList, { type StepTabItem } from '../../components/ui/StepTabsList';
import DataTable from '../../components/ui/DataTable';
import Textarea from '../../components/ui/Textarea';
import { formatDate } from '../../lib/utils';
import CampaignChat from '../../components/CampaignChat';
import JobOperationsTab from '../../components/JobOperationsTab';
import JobContractsTab from '../../components/JobContractsTab';
import { GoogleWorkspaceJobPanel } from '../../components/google/GoogleWorkspaceJobPanel';
import { jobRouteParam } from '../../lib/slug';

const CORP_JOB_TABS = new Set([
  'brief',
  'shortlist',
  'approved',
  'scripts',
  'submissions',
  'progress',
  'contracts',
  'chat',
  'operations',
]);

export default function CorporateJobDetail() {
  const { id: rawId } = useParams();
  const jobParam = jobRouteParam(rawId);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeMainTab = tabParam && CORP_JOB_TABS.has(tabParam) ? tabParam : 'brief';
  const setActiveMainTab = (v: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === 'brief') next.delete('tab');
        else next.set('tab', v);
        return next;
      },
      { replace: true },
    );
  };
  const [job, setJob] = useState<Job | null>(null);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);

  // Script & Submission state
  const [scripts, setScripts] = useState<any[]>([]);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewingScriptId, setReviewingScriptId] = useState<string | null>(null);
  const [reviewingSubmissionId, setReviewingSubmissionId] = useState<string | null>(null);

  useEffect(() => {
    void loadJob();
  }, [jobParam]);

  const loadJob = async () => {
    if (!jobParam) return;
    try {
      const jobRes = await api.get(`/jobs/${encodeURIComponent(jobParam)}`);
      const jid = jobRes.data.id as string;
      setJob(jobRes.data);
      const invitationsRes = await api.get(`/invitations?jobId=${encodeURIComponent(jid)}`);
      setInvitations(invitationsRes.data);
      void loadScripts(jid);
      void loadSubmissions(jid);
    } catch (error) {
      console.error('Failed to load job:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadScripts = async (jid: string) => {
    try {
      const response = await api.get('/scripts', { params: { jobId: jid } });
      setScripts(response.data);
    } catch (error) {
      console.error('Failed to load scripts:', error);
    }
  };

  const loadSubmissions = async (jid: string) => {
    try {
      const response = await api.get('/submissions');
      const filtered = response.data.filter((sub: any) => sub.invitation?.job?.id === jid);
      setSubmissions(filtered);
    } catch (error) {
      console.error('Failed to load submissions:', error);
    }
  };

  const handleApprove = async (invitationId: string, approve: boolean) => {
    try {
      await api.post(`/invitations/${invitationId}/approve-production`, { isApproved: approve });
      await loadJob();
    } catch (error) {
      console.error('Failed to approve:', error);
    }
  };

  const handleApproveScript = async (scriptId: string) => {
    try {
      await api.post(`/scripts/${scriptId}/approve`);
      if (job) await loadScripts(job.id);
      setReviewingScriptId(null);
      alert('Script approved!');
    } catch (error) {
      console.error('Failed to approve script:', error);
      alert('Failed to approve script');
    }
  };

  const handleRequestScriptRevision = async (scriptId: string) => {
    if (!reviewComment.trim()) {
      alert('Please provide revision comments');
      return;
    }

    try {
      await api.post(`/scripts/${scriptId}/request-revision`, { reviewComment });
      if (job) await loadScripts(job.id);
      setReviewingScriptId(null);
      setReviewComment('');
      alert('Revision requested!');
    } catch (error) {
      console.error('Failed to request revision:', error);
      alert('Failed to request revision');
    }
  };

  const handleApproveSubmission = async (submissionId: string) => {
    try {
      await api.post(`/submissions/${submissionId}/approve`);
      if (job) await loadSubmissions(job.id);
      setReviewingSubmissionId(null);
      alert('Submission approved!');
    } catch (error) {
      console.error('Failed to approve submission:', error);
      alert('Failed to approve submission');
    }
  };

  const handleRequestSubmissionRevision = async (submissionId: string) => {
    if (!reviewComment.trim()) {
      alert('Please provide revision comments');
      return;
    }

    try {
      await api.post(`/submissions/${submissionId}/request-revision`, { revisionComments: reviewComment });
      if (job) await loadSubmissions(job.id);
      setReviewingSubmissionId(null);
      setReviewComment('');
      alert('Revision requested!');
    } catch (error) {
      console.error('Failed to request revision:', error);
      alert('Failed to request revision');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12">Loading...</div>;
  }

  if (!job) {
    return <div className="text-center py-12 text-gray-500">Job not found</div>;
  }

  const shortlistedInvitations = invitations.filter(inv => inv.isShortlisted);
  const approvedInvitations = invitations.filter(inv => inv.isApprovedForProduction);
  const isReadyForApproval = job.shortlistedCount >= (job.estimatedKOL || 0) && job.approvedTalentCount === 0;

  const statusTooltips: Record<string, string> = {
    DRAFT: 'Campaign is being prepared. Not yet visible to KOLs.',
    OPEN: 'Campaign is active and accepting KOL applications.',
    ACTIVE: 'Campaign is in production with assigned KOLs.',
    PAUSED: 'Campaign is temporarily on hold.',
    COMPLETED: 'All deliverables have been submitted and approved.',
    ARCHIVED: 'Campaign is closed and archived.',
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm text-gray-500">
        <button onClick={() => navigate('/brand/jobs')} className="hover:text-purple-600 transition-colors">Jobs</button>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-medium truncate max-w-md">{job.title}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/brand/jobs')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title">{job.title}</h1>
            <p className="text-gray-600 mt-1">Campaign Details</p>
          </div>
        </div>
        <Badge variant={job.status === 'ACTIVE' ? 'success' : 'secondary'} title={statusTooltips[job.status] || ''}>
          {job.status}
        </Badge>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-blue-600">{job.broadcastedCount}</div>
            <div className="text-sm text-gray-600">Broadcasted</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-green-600">{job.acceptedCount}</div>
            <div className="text-sm text-gray-600">Accepted</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-purple-600">{job.shortlistedCount}</div>
            <div className="text-sm text-gray-600">Shortlisted</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-orange-600">{job.approvedTalentCount}</div>
            <div className="text-sm text-gray-600">Approved</div>
          </CardContent>
        </Card>
      </div>

      {/* Approval Alert */}
      {isReadyForApproval && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Shortlist Ready for Approval</h3>
                <p className="text-sm text-gray-700">
                  The agency has shortlisted {job.shortlistedCount} KOLs for this campaign. 
                  Please review and approve to proceed with production.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="brief" value={activeMainTab} onValueChange={setActiveMainTab}>
        {(() => {
          const targetKols = job.estimatedKOL || 0;
          const allScriptsApproved =
            scripts.length > 0 && scripts.every((s: { status?: string }) => s.status === 'APPROVED_BY_BRAND');
          const allSubmissionsApproved =
            submissions.length > 0 && submissions.every((s: { status?: string }) => s.status === 'APPROVED');
          const progressSteps: StepTabItem[] = [
            { value: 'brief', label: 'Brief', done: true },
            {
              value: 'shortlist',
              label: 'Shortlist',
              done: targetKols > 0 ? job.shortlistedCount >= targetKols : job.shortlistedCount > 0,
              badge: isReadyForApproval ? (
                <Badge variant="warning" className="ml-1">
                  Approval Needed
                </Badge>
              ) : undefined,
            },
            {
              value: 'approved',
              label: 'Approved KOLs',
              done: targetKols > 0 ? job.approvedTalentCount >= targetKols : job.approvedTalentCount > 0,
            },
            {
              value: 'scripts',
              label: `Scripts (${scripts.length})`,
              done: allScriptsApproved,
            },
            {
              value: 'submissions',
              label: `Submissions (${submissions.length})`,
              done: allSubmissionsApproved,
            },
          ];
          return (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <StepTabsList
                steps={progressSteps}
                activeValue={activeMainTab}
                onSelect={setActiveMainTab}
                className="flex-1"
              />
              <TabsList className="ml-auto flex h-auto min-h-10 flex-wrap items-center gap-1 [&>button]:shrink-0">
                <TabsTrigger value="progress">Progress</TabsTrigger>
                <TabsTrigger value="contracts">Contracts</TabsTrigger>
                <TabsTrigger value="chat">
                  <MessageCircle className="w-4 h-4 mr-1" />
                  Chat
                </TabsTrigger>
                <TabsTrigger value="operations">Operations</TabsTrigger>
              </TabsList>
            </div>
          );
        })()}

        {/* Brief */}
        <TabsContent value="brief">
          <Card>
            <CardContent className="p-6 space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{job.description}</p>
              </div>

              <GoogleWorkspaceJobPanel jobId={job.id} readOnly className="shadow-none border-gray-200" />

              {job.demoVideoLink && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Demo Video</h3>
                  <a href={job.demoVideoLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {job.demoVideoLink}
                  </a>
                </div>
              )}

              {job.rewardsJson && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Rewards</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {(job.rewardsJson as any).cash > 0 && (
                      <div className="p-3 bg-green-50 rounded-lg">
                        <div className="text-sm text-gray-600">Cash</div>
                        <div className="text-lg font-semibold text-green-600">RM {(job.rewardsJson as any).cash}</div>
                      </div>
                    )}
                    {(job.rewardsJson as any).product && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <div className="text-sm text-gray-600">Product</div>
                        <div className="text-lg font-semibold text-blue-600">{(job.rewardsJson as any).product}</div>
                      </div>
                    )}
                    {(job.rewardsJson as any).commission > 0 && (
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <div className="text-sm text-gray-600">Commission</div>
                        <div className="text-lg font-semibold text-purple-600">{(job.rewardsJson as any).commission}%</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {job.products && job.products.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Products</h3>
                  <div className="space-y-3">
                    {job.products.map((product) => (
                      <div key={product.id} className="p-4 border rounded-lg">
                        <div className="font-medium text-gray-900">{product.title}</div>
                        {product.description && (
                          <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Shortlist */}
        <TabsContent value="shortlist">
          <Card>
            <CardContent className="p-6">
              {shortlistedInvitations.length === 0 ? (
                <p className="text-center py-8 text-gray-500">
                  No KOLs shortlisted yet. The agency is working on selecting candidates.
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                      {job.shortlistedCount} KOL{job.shortlistedCount !== 1 ? 's' : ''} shortlisted. 
                      {isReadyForApproval && ' Ready for your approval to proceed with production.'}
                    </p>
                  </div>

                  <DataTable
                    data={shortlistedInvitations}
                    columns={[
                      {
                        header: 'KOL',
                        accessor: (row: Invitation) => (
                          <div>
                            <div className="font-medium text-gray-900">{row.kolProfile?.name}</div>
                            <div className="text-sm text-gray-500">{row.kolProfile?.email}</div>
                          </div>
                        ),
                      },
                      {
                        header: 'Status',
                        accessor: (row: Invitation) => (
                          <div className="space-y-1">
                            <Badge variant="success">Shortlisted</Badge>
                            {row.isApprovedForProduction && (
                              <Badge variant="success" className="ml-2">Approved</Badge>
                            )}
                          </div>
                        ),
                      },
                      {
                        header: 'Action',
                        accessor: (row: Invitation) => (
                          <div className="flex gap-2">
                            {!row.isApprovedForProduction ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApprove(row.id, true);
                                  }}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleApprove(row.id, false);
                                  }}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </>
                            ) : (
                              <Badge variant="success">Approved</Badge>
                            )}
                          </div>
                        ),
                      },
                    ]}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Approved */}
        <TabsContent value="approved">
          <Card>
            <CardContent className="p-6">
              {approvedInvitations.length === 0 ? (
                <p className="text-center py-8 text-gray-500">
                  No approved KOLs yet. Approve shortlisted KOLs to start production.
                </p>
              ) : (
                <DataTable
                  data={approvedInvitations}
                  columns={[
                    {
                      header: 'KOL',
                      accessor: (row: Invitation) => (
                        <div>
                          <div className="font-medium text-gray-900">{row.kolProfile?.name}</div>
                          <div className="text-sm text-gray-500">{row.kolProfile?.email}</div>
                        </div>
                      ),
                    },
                    {
                      header: 'Approved',
                      accessor: (row: Invitation) => formatDate(row.updatedAt),
                    },
                    {
                      header: 'Status',
                      accessor: () => <Badge variant="success">In Production</Badge>,
                    },
                  ]}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scripts */}
        <TabsContent value="scripts">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Script Review
              </CardTitle>
            </CardHeader>
            <CardContent>
              {scripts.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No scripts submitted yet</p>
              ) : (
                <div className="space-y-6">
                  {scripts.map((script) => (
                    <Card key={script.id} className="border-2">
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          {/* Header */}
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="font-medium text-gray-900">
                                {script.invitation?.kolProfile?.name || 'Unknown KOL'}
                              </div>
                              <div className="text-sm text-gray-500">Version {script.version}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={
                                  script.status === 'APPROVED_BY_BRAND'
                                    ? 'success'
                                    : script.status === 'SUBMITTED_FOR_BRAND'
                                    ? 'warning'
                                    : script.status === 'REVISION_REQUESTED'
                                    ? 'destructive'
                                    : 'secondary'
                                }
                              >
                                {script.status}
                              </Badge>
                              <span className="text-sm text-gray-500">{formatDate(script.createdAt)}</span>
                            </div>
                          </div>

                          {/* Script Content */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Script Content:</h4>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap font-mono">{script.content}</p>
                          </div>

                          {/* Previous Review Comment */}
                          {script.reviewComment && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                              <div className="text-sm font-medium text-yellow-900 mb-1">Previous Review:</div>
                              <p className="text-sm text-yellow-800">{script.reviewComment}</p>
                            </div>
                          )}

                          {/* Actions */}
                          {script.status === 'SUBMITTED_FOR_BRAND' && (
                            <div className="border-t pt-4">
                              {reviewingScriptId === script.id ? (
                                <div className="space-y-3">
                                  <Textarea
                                    value={reviewComment}
                                    onChange={(e) => setReviewComment(e.target.value)}
                                    placeholder="Enter revision comments..."
                                    rows={3}
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleRequestScriptRevision(script.id)}
                                    >
                                      Submit Revision Request
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setReviewingScriptId(null);
                                        setReviewComment('');
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleApproveScript(script.id)}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Approve Script
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setReviewingScriptId(script.id)}
                                  >
                                    <MessageCircle className="w-4 h-4 mr-1" />
                                    Request Revision
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Submissions */}
        <TabsContent value="submissions">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Video className="w-5 h-5" />
                Content Review
              </CardTitle>
            </CardHeader>
            <CardContent>
              {submissions.length === 0 ? (
                <p className="text-center py-8 text-gray-500">No content submitted yet</p>
              ) : (
                <div className="space-y-6">
                  {submissions.map((submission) => (
                    <Card key={submission.id} className="border-2">
                      <CardContent className="p-6">
                        <div className="space-y-4">
                          {/* Header */}
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="font-medium text-gray-900">
                                {submission.invitation?.kolProfile?.name || 'Unknown KOL'}
                              </div>
                              <div className="text-sm text-gray-500">Version {submission.version}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge
                                variant={
                                  submission.status === 'APPROVED'
                                    ? 'success'
                                    : submission.status === 'SUBMITTED'
                                    ? 'warning'
                                    : submission.status === 'REVISION_REQUESTED'
                                    ? 'destructive'
                                    : 'secondary'
                                }
                              >
                                {submission.status}
                              </Badge>
                              <span className="text-sm text-gray-500">{formatDate(submission.createdAt)}</span>
                            </div>
                          </div>

                          {/* Caption */}
                          {submission.captionText && (
                            <div className="bg-blue-50 rounded-lg p-3">
                              <h4 className="text-sm font-medium text-blue-900 mb-1">Caption:</h4>
                              <p className="text-sm text-blue-800">{submission.captionText}</p>
                            </div>
                          )}

                          {/* Media */}
                          {submission.media && submission.media.length > 0 && (
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">Media Files:</h4>
                              <div className="grid grid-cols-1 gap-2">
                                {submission.media.map((media: any) => {
                                  const isExternalLink = media.url.startsWith('http://') || media.url.startsWith('https://');
                                  const isYouTube = media.url.includes('youtube.com') || media.url.includes('youtu.be');
                                  const isDrive = media.url.includes('drive.google.com');
                                  
                                  return (
                                    <a
                                      key={media.id}
                                      href={media.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 transition"
                                    >
                                      {isYouTube ? (
                                        <>
                                          <Video className="w-6 h-6 text-red-600 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-gray-900">YouTube Video</div>
                                            {media.title && <div className="text-xs text-gray-500">{media.title}</div>}
                                          </div>
                                        </>
                                      ) : isDrive ? (
                                        <>
                                          <ExternalLink className="w-6 h-6 text-blue-600 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-gray-900">Google Drive File</div>
                                            {media.title && <div className="text-xs text-gray-500">{media.title}</div>}
                                          </div>
                                        </>
                                      ) : media.type === 'VIDEO' ? (
                                        <>
                                          <Video className="w-6 h-6 text-purple-600 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-gray-900">
                                              Video {isExternalLink ? 'Link' : 'File'}
                                            </div>
                                            {media.title && <div className="text-xs text-gray-500">{media.title}</div>}
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <span className="text-2xl flex-shrink-0">🖼️</span>
                                          <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-gray-900">
                                              Image {isExternalLink ? 'Link' : 'File'}
                                            </div>
                                            {media.title && <div className="text-xs text-gray-500">{media.title}</div>}
                                          </div>
                                        </>
                                      )}
                                      <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    </a>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Previous Review Comment */}
                          {submission.revisionComments && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                              <div className="text-sm font-medium text-yellow-900 mb-1">Previous Review:</div>
                              <p className="text-sm text-yellow-800">{submission.revisionComments}</p>
                            </div>
                          )}

                          {/* Actions */}
                          {submission.status === 'SUBMITTED' && (
                            <div className="border-t pt-4">
                              {reviewingSubmissionId === submission.id ? (
                                <div className="space-y-3">
                                  <Textarea
                                    value={reviewComment}
                                    onChange={(e) => setReviewComment(e.target.value)}
                                    placeholder="Enter revision comments..."
                                    rows={3}
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleRequestSubmissionRevision(submission.id)}
                                    >
                                      Submit Revision Request
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setReviewingSubmissionId(null);
                                        setReviewComment('');
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleApproveSubmission(submission.id)}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-1" />
                                    Approve Content
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setReviewingSubmissionId(submission.id)}
                                  >
                                    <MessageCircle className="w-4 h-4 mr-1" />
                                    Request Revision
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Progress */}
        <TabsContent value="progress">
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="font-medium">Broadcast Phase</span>
                  <Badge variant="success">Completed</Badge>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="font-medium">Shortlisting</span>
                  <Badge variant={job.shortlistedCount >= (job.estimatedKOL || 0) ? 'success' : 'secondary'}>
                    {job.shortlistedCount >= (job.estimatedKOL || 0) ? 'Completed' : 'In Progress'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="font-medium">Approval</span>
                  <Badge variant={job.approvedTalentCount > 0 ? 'success' : 'secondary'}>
                    {job.approvedTalentCount > 0 ? 'In Progress' : 'Pending'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <span className="font-medium">Production</span>
                  <Badge variant="secondary">Pending</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="contracts">
          {job.managedByOrgId ? (
            job && <JobContractsTab jobId={job.id} agencyOrgId={job.managedByOrgId} />
          ) : (
            <Card>
              <CardContent className="p-6">
                <p className="text-sm text-gray-600">
                  Contracts need a managing agency on this job. Templates are owned by the agency; once assigned, your team can generate drafts here.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="chat">
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-2">Campaign room</h3>
              <p className="text-sm text-gray-600 mb-4">Messages for brand and agency users on this job.</p>
              {job && <CampaignChat jobId={job.id} />}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="operations">
          <Card>
            <CardContent className="p-6">{job && <JobOperationsTab jobId={job.id} />}</CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
